import FavoriteRestaurant from "@core/favoriteRestaurant";
import { STATE } from "@core/state";
import windmill from "@core/windmill";
import { $, $$, html } from "@utils/dom";
import { on, off } from "@utils/listener";
import { motion_reduced } from "@utils/mobile";

export const OPEN_CLASSNAME = "--js-site-nav-opened";
export const CLOSE_CLASSNAME = "--js-site-nav-closing";

class SiteNav {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;
    this.scroller = $('.site-nav__scroller', this.el);
    this.menuLink = $('.site-nav__menuLink > a', this.el);
    this.bookingLink = $('.site-nav__bookingLink', this.el);
    this.triggers = [...$$(`[aria-controls="${this.el.id}"]`)];

    this._fallbackMenuLinkURL = this.menuLink?.href;
    this._fallbackBookingURL = this.bookingLink?.href;
    this._initialized = false;
    this._opened = false;
    this._scrollY = 0;

    this._handleTriggers = this._handleTriggers.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._onCloseCompleted = this._onCloseCompleted.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onFavoriteRestaurantUpdate = this._onFavoriteRestaurantUpdate.bind(this);
    this._onWindmillExit = this._onWindmillExit.bind(this);
  }

  init() {
    if( this._initialized ) return;
    this._initialized = true;
    
    [ ...$$('.site-nav__menu li', this.el) ].sort((a, b) => {
      const aHasImg = $('img', a);
      const bHasImg = $('img', b);

      if( aHasImg && bHasImg ) return 0;
      else if( aHasImg && !bHasImg ) return -1;
      else return 1;
    }).forEach((listItem, index, array) => {
      listItem.style.setProperty('--md-index', index);
      listItem.style.setProperty('--md-revindex', array.length - index - 1);
    });

    this._bindEvents();
  }
  open() {
    // if SiteNav is already opened, do nothing
    if (this._opened === true) return;
    this._opened = true;

    // save scrollY & scroll site-nav to top
    this._scrollY = window.scrollY;
    this.scroller.scrollTo({ top: 0, behavior: 'instant' });

    // dispatch to state current status
    STATE.dispatch("SITE_NAV", this._opened);

    // stop page scroll & site-nav open
    this.emitter.emit("SiteScroll.stop", true);
    this.emitter.emit("SiteNav.open");

    // inform each triggers that SiteNav is opened
    this.triggers.forEach(btn => {
      btn.setAttribute("aria-expanded", true);
      btn.classList.add("is-active");
    });

    // remove close animation completion listener
    off(this.scroller, 'transitionend', this._onCloseCompleted);

    // set SiteNav visible for screen readers
    this.el.removeAttribute("inert");

    // inform <html> that SiteNav is opened
    if( html.classList.contains(CLOSE_CLASSNAME) ) html.classList.replace(CLOSE_CLASSNAME, OPEN_CLASSNAME);
    else html.classList.add(OPEN_CLASSNAME);
  }
  close() {
    // if SiteNav isn't opened, do nothing
    if (this._opened === false) return;
    this._opened = false;

    // dispatch to state current status
    STATE.dispatch("SITE_NAV", this._opened);

    // inform triggers that SiteNav is closed
    this.triggers.forEach(btn => {
      btn.setAttribute("aria-expanded", false);
      btn.classList.remove("is-active");
    });

    // restore previous scrollY
    window.scrollTo({top: this._scrollY, behavior: 'auto'});
    
    // update site-scroll, restart site-scroll & close site-nav
    //this.emitter.emit('SiteScroll.update');
    this.emitter.emit("SiteScroll.start");
    this.emitter.emit("SiteNav.close");

    // make sure event listener is not duplicated
    off(this.scroller, 'transitionend', this._onCloseCompleted);
    on(this.scroller, 'transitionend', this._onCloseCompleted);

    // inform <html> that SiteNav is closed
    html.classList.replace(OPEN_CLASSNAME, CLOSE_CLASSNAME);

    // if user prefer reduced motion, go to close transition callback immediately
    if( motion_reduced ) this._onCloseCompleted({ event: this.scroller });
  }
  toggle() {
    // regular SiteNav toggle handling
    if (this._opened === true) this.close();
    else this.open();
  }

  _bindEvents() {
    FavoriteRestaurant.on('update', this._onFavoriteRestaurantUpdate);
    this._onFavoriteRestaurantUpdate();
    windmill.on('exiting', this._onWindmillExit);

    if( this.triggers ) on(this.triggers, "click", this._handleTriggers);
    if( this.el ) on(window, "keydown", this._handleKeyDown);
    
    this.emitter.on("SiteBackdrop.click", this._onBackdropClick);
    this.emitter.on("SiteNav.toggle", this._handleTriggers);
  }

  _handleTriggers(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.toggle();
  }
  _handleKeyDown(event) {
    if (this._opened === true && (event.key === "Escape" || event.key === "Esc")) {
      this.close();
    }
  }
  _onCloseCompleted(event) {
    if (event.target !== this.scroller || this._opened === true) return;

    // remove event listener
    off(this.scroller, 'transitionend', this._onCloseCompleted);

    // hide SiteNav from screen readers
    this.el.setAttribute("inert", true);
  }
  _onBackdropClick() {
    if( this._opened ) this.close();
  }
  _onFavoriteRestaurantUpdate() {
    if( this.menuLink ) this.menuLink.href = FavoriteRestaurant.menu_url ? FavoriteRestaurant.menu_url : this._fallbackMenuLinkURL;
    if( this.bookingLink ) {
      this.bookingLink.href = FavoriteRestaurant.booking_url ? FavoriteRestaurant.booking_url : this._fallbackBookingURL;
      this.bookingLink.setAttribute('aria-hidden', this.bookingLink.href.endsWith('#'));
    }
  }
  _onWindmillExit() {
    if( this.opened ) this.close();
  }

  // getters
  get opened() {
    return this._opened;
  }
}

export default SiteNav;
